import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { BotSequenceType } from "./BotSequence.Enums";
import { BotSequenceEnrollmentInterface, BotSequenceReconcileRequestInterface, BotSequenceReconcileRunInterface } from "./BotSequence.Interfaces";
import { BotSequenceDeliveryKey, BotSequenceScheduledAttemptInterface } from "./BotSequenceScheduler.Interfaces";

// LINK apps/bull/src/Services/BotSequences/TestRun/BotSequenceTestRun.Service.ts

/** Lifecycle of a manual full-flow test run (docs/specs/new-sequence-approach/full-flow-test-run.md). */
export enum BotSequenceTestRunStatus {
  ACTIVE = "active",
  COMPLETED = "completed",
  CANCELLED = "cancelled"
}

/** Stages the pipeline stamps on the run as the real machinery carries it forward. */
export type BotSequenceTestRunStage =
  | "created"
  | "initial_diff_applied"
  | "reconcile_requested"
  | "booked"
  | "jit_diff_applied"
  | "jit_sibling_applied"
  | "send_simulated"
  | "completed"
  /** The run was cancelled; `detail.cancelledAttemptIds` lists the booked attempts cancelled with it (decision 68). */
  | "cancelled";

export interface BotSequenceTestRunCheckpoint {
  stage: BotSequenceTestRunStage;
  at: string;
  /** Free-form stage evidence (attemptId, outbox row id, applied field names…). */
  detail?: Record<string, unknown>;
}

/**
 * A simulated FMS diff: partial normalized lease/lead fields overlaid on the current DB
 * row and persisted through the REAL sync services — never a direct entity save, so the
 * reconcile outbox row plants itself exactly as on a live FMS sync. JSON wire shape:
 * date fields ride as ISO strings.
 */
export type BotSequenceTestRunDiff = Record<string, unknown>;

/**
 * A simulated SIBLING lead (ENG-3416, storedge-lead-sync.md B8/B10): served to the JIT refresh
 * as the location's leads changes feed instead of a vendor call, so the sync-time sibling rule
 * can flip the pinned inquiry exactly as a real feed would. Completed from the pinned lead
 * (org, location, FMS type, phone, tenant) — only the vendor payload and the created date are
 * authored. Meaningful only where the lead row carries `jitChangesRefresh` (StorEdge today):
 * other FMSs never pull a feed, so the input is ignored and the preflight says so.
 */
export interface BotSequenceTestRunSiblingLead {
  /** Vendor payload fields of the sibling — for StorEdge `status` (`reserved` / `moved_in`) and `reserved_at`. */
  raw: Record<string, unknown>;
  /** ISO date; must be on or after the pinned lead's `leadCreatedDate` to count as a conversion. */
  leadCreatedDate: string;
  /** Defaults to `test-run-<runId>-sibling`. */
  externalLeadId?: string;
  /** Defaults to the pinned lead's unit. */
  externalUnitId?: string;
}

/**
 * A stand-in for the location's sequence config while a run is live — merged ON TOP of
 * any resolved SequenceConfig (ENG-2987) for the run's own location + type, and scoped
 * to the one user under test. It is the third and last faked input; the field and its
 * merge are deleted in the key-based-cursor follow-up to ENG-2987, once test runs have
 * a safer authoring path than a location-wide real config row.
 *
 * Shape mirrors `BotSequenceLocationTypeConfig` so the merge is a direct assignment.
 */
export interface BotSequenceTestRunConfigOverride {
  /** Defaults to ENABLED. SIMULATED also dispatches — as a dry run whose deliveries the Outbound Gate withholds (ENG-3373). */
  mode?: string;
  /** Step offsets in days from the anchor, e.g. `[3, 5, 7]`. Required — an empty ladder books nothing. */
  ladderOffsetDays: number[];
  /** Per-enrollment minimum days between fires; defaults to 0 (off). */
  minGapDays?: number;
  /** Recipient-local send window, replacing the location's "sequence" schedule for this run; omitted = the schedule governs. */
  window?: { startHour: number; startMinute: number; endHour: number; endMinute: number };
}

/**
 * One manual full-flow test run of the new Sequences engine: fakes exactly two things —
 * FMS data (initialDiff at creation, jitDiff at fire time) and the outward send — while
 * every stage in between is the real production machinery on real rows. Non-prod only.
 */
export interface BotSequenceTestRunInterface extends OrgBaseInterface {
  userId: number;
  sequenceType: BotSequenceType;
  /** Required for PAST_DUE runs. */
  leaseId: number | null;
  /** Required for ABANDONED_CART runs. */
  leadId: number | null;
  /** Backfilled once the reconciler has created/matched the enrollment. */
  enrollmentId: number | null;
  status: BotSequenceTestRunStatus;
  /** Skip the bot fire request and finalize `fired` + cursor via the shared transition. */
  simulateSend: boolean;
  /** Book `fireAt = now` and skip the recheck's send-window gate (all other gates stay hard). */
  bypassComplianceWindow: boolean;
  /** Applied at creation to make the user qualify (e.g. past `paidThroughDate`). */
  initialDiff: BotSequenceTestRunDiff | null;
  /** Served to the handler's JIT refresh as the by-id fetch result instead of a vendor call. */
  jitDiff: BotSequenceTestRunDiff | null;
  /** Served to the JIT refresh as the location's changes feed (StorEdge only — see the type). */
  jitSiblingLead: BotSequenceTestRunSiblingLead | null;
  /** Stamped once the JIT refresh consumed the static inputs (diff and/or sibling). */
  jitDiffConsumedAt: Date | null;
  /** Stands in for the location's sequence config while the run is live, scoped to the run's user. */
  configOverride: BotSequenceTestRunConfigOverride | null;
  checkpoints: BotSequenceTestRunCheckpoint[];
  /** Hard stop: every seam lookup ignores the run past this moment — a forgotten run cannot leak into tomorrow. */
  expiresAt: Date;
}

/**
 * Which row a test run's subject is, per type. Every type but the cart hangs off a lease:
 * PAST_DUE reads its payment fields, MOVE_IN/MOVE_OUT their move dates. Declared as an
 * exhaustive Record so adding a sequence type fails to compile until it is answered here,
 * rather than silently falling into the lead branch.
 */
export const BOT_SEQUENCE_TEST_RUN_SUBJECT: Record<BotSequenceType, "lease" | "lead"> = {
  [BotSequenceType.PAST_DUE]: "lease",
  [BotSequenceType.ABANDONED_CART]: "lead",
  [BotSequenceType.MOVE_IN]: "lease",
  [BotSequenceType.MOVE_OUT]: "lease"
};

/**
 * The subject field each type anchors Day 0 on — what an `initialDiff` must set to place
 * the subject on the ladder. NOTE the one asymmetry: PAST_DUE's Day 0 is the DUE date,
 * i.e. `paidThroughDate + 1` local day, while the others anchor on the date itself.
 */
export const BOT_SEQUENCE_TEST_RUN_ANCHOR_FIELD: Record<BotSequenceType, string> = {
  [BotSequenceType.PAST_DUE]: "paidThroughDate",
  [BotSequenceType.ABANDONED_CART]: "leadCreatedDate",
  [BotSequenceType.MOVE_IN]: "moveInDate",
  [BotSequenceType.MOVE_OUT]: "moveOutDate"
};

// ── API shapes ─────────────────────────────────────────────────────────────────────
// Shared because the runs are created from bull directly (curl / the test script) AND
// proxied by the server for the Studio admin page — both sides need the same contract.

/** Request body of `POST /bot-sequences/org/{orgId}/test-run`. */
export interface CreateTestRunBody {
  userId: number;
  sequenceType: BotSequenceType;
  /** Required for PAST_DUE. */
  leaseId?: number;
  /** Required for ABANDONED_CART. */
  leadId?: number;
  /** Simulated FMS diff applied at creation (e.g. a past paidThroughDate to qualify the user). */
  initialDiff?: BotSequenceTestRunDiff;
  /** Simulated FMS diff served to the handler's JIT refresh as the by-id fetch result. */
  jitDiff?: BotSequenceTestRunDiff;
  /** Simulated sibling lead served to the JIT refresh as the changes feed (ABANDONED_CART, StorEdge only). */
  jitSiblingLead?: BotSequenceTestRunSiblingLead;
  /** Default true: skip the bot fire request, finalize `fired` via the shared transition. */
  simulateSend?: boolean;
  /** Default true: book fireAt = now and skip the recheck's send-window gate. */
  bypassComplianceWindow?: boolean;
  /** Default 60. */
  expiresInMinutes?: number;
  /**
   * TEMPORARY (ENG-3002): stands in for the location's sequence config. Without it a run
   * on an unconfigured location has no ladder and the scheduler books nothing — and unlike
   * a real SequenceConfig row it is scoped to this run's location + sequence type + USER,
   * so a synthetic ladder can never reach the location's real population.
   */
  configOverride?: BotSequenceTestRunConfigOverride;
  /**
   * Outbound Gate plan decision 6: with `simulateSend: false`, a run whose verdict lines
   * contain a REAL delivery is refused unless this is true. The refusal carries the exact
   * lines, so the operator reads what will leave Swivl before ticking the confirmation.
   */
  allowReal?: boolean;
}

/** One delivery the day would make, and what the Outbound Gate says about it right now. */
export interface TestRunDeliveryVerdict {
  kind: BotSequenceDeliveryKey;
  /** The phone, the email, or `<FMS> tenant <external id>`; null when the user has none. */
  recipient: string | null;
  /** Who that is — the person's name, or the tenant's — and the facility. */
  who: string;
  result: "real" | "simulated" | "blocked";
  /** The gate's reason (`flag_off`, `not_listed`, `sequence_simulated`, `opted_out`, …). */
  reason: string;
  /** The finished line, e.g. `SMS → +19174069134 (Chuck Reina, Merlin): real`. */
  line: string;
}

/**
 * The gates of the new engine, in the order they apply (engine-gates.md, gate matrix rows
 * 1–6): master → reconciler → scheduler → location mode → dispatcher → JIT. The first "off"
 * in the chain is where the run stops; JIT is never a stop.
 */
export type TestRunPreflightStop = "master" | "reconciler" | "scheduler" | "mode" | "dispatcher";

/** The gates the run depends on — surfaced, deliberately never bypassed. */
export interface TestRunPreflight {
  /** The org's flags as set (raw presence). The effective rule is master AND module, folded into `stopsAt`. */
  engineEnabled: boolean;
  reconcilerEnabled: boolean;
  schedulerEnabled: boolean;
  /** The location's config mode for the run's type; SIMULATED runs the day as a dry run (every delivery withheld) and is not a stop. */
  typeMode: string | null;
  dispatcherEnabled: boolean;
  jitEnabled: boolean;
  /** The first gate in chain order that is off — the run halts there; null = the run can reach a fire. */
  stopsAt: TestRunPreflightStop | null;
  /**
   * True when the run carries static JIT inputs (jitDiff / jitSiblingLead): the handler then
   * treats JIT as ON for this run — rollout switch, integration setting and freshness window
   * are bypassed for the run's subject only — and runs the real refresh on the stubbed vendor calls.
   */
  jitStaticInputs: boolean;
  /** One line per off gate, the stopping gate first; a JIT-off line is an advisory. */
  warnings: string[];
  /** The ladder step that would fire today for this anchor; null when nothing is due (the verdicts then cover the whole ladder). */
  dueStep: number | null;
  /** Outbound Gate plan decision 6: what each delivery of that day would do for THIS user, decided now. */
  verdicts: TestRunDeliveryVerdict[];
}

export interface CreateTestRunResult {
  run: BotSequenceTestRunInterface;
  reconcileRequest: BotSequenceReconcileRequestInterface | null;
  preflight: TestRunPreflight;
}

/** The run's checkpoints plus every pipeline row it produced — the full-flow outcome view. */
export interface BotSequenceTestRunTrace {
  run: BotSequenceTestRunInterface;
  reconcileRequest: BotSequenceReconcileRequestInterface | null;
  reconcileRuns: BotSequenceReconcileRunInterface[];
  enrollments: BotSequenceEnrollmentInterface[];
  attempts: BotSequenceScheduledAttemptInterface[];
}
